import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, streamChat } from "../lib/api";
import { Send, Bot, User, Sparkles, Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MAX_RECORD_MS = 60000;

const SUGGESTIONS = [
  "What are the night-cap rules in Los Angeles?",
  "Do I need a permit for a hosted stay under 30 nights in NYC?",
  "How do I calculate Miami Beach resort tax on a $250/night rental?",
  "What safety equipment does Nashville require?",
];

export default function Assistant() {
  const [params] = useSearchParams();
  const cityFromUrl = params.get("city");
  const propertyFromUrl = params.get("property");
  const [sessionId] = useState(`hg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [cities, setCities] = useState([]);
  const [cityContext, setCityContext] = useState(cityFromUrl || "");
  const scrollRef = useRef(null);

  const [usage, setUsage] = useState(null);
  const refreshUsage = () => api.get("/ai/usage").then((r) => setUsage(r.data)).catch(() => {});
  useEffect(() => { api.get("/cities").then((r) => setCities(r.data)); refreshUsage(); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, streaming]);

  const send = async (text) => {
    if (!text.trim() || streaming) return;
    setInput("");
    const userId = `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const asstId = `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setMessages((m) => [...m, { id: userId, role: "user", content: text }, { id: asstId, role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      await streamChat({
        message: text,
        city_slug: cityContext || undefined,
        property_id: propertyFromUrl || undefined,
        session_id: sessionId,
        onDelta: (delta) => setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + delta };
          return copy;
        }),
      });
    } catch (e) {
      const content = e.status === 429 ? e.message : "Sorry — there was an error. Please try again.";
      setMessages((m) => [...m.slice(0, -1), { id: asstId, role: "assistant", content }]);
    } finally { setStreaming(false); refreshUsage(); }
  };

  return (
    <div className="animate-slide-up flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-80px)]" data-testid="assistant-page">
      <div className="flex items-center justify-between flex-wrap gap-3 pb-4 border-b border-slate-200">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Regulatory AI · Claude Sonnet 5</div>
          <h1 className="text-2xl font-bold text-slate-900 font-display">AI Compliance Assistant</h1>
        </div>
        <label className="text-sm text-slate-600 flex items-center gap-2">
          <span className="font-mono uppercase tracking-widest text-[10px]">City context</span>
          <select value={cityContext} onChange={(e) => setCityContext(e.target.value)} data-testid="chat-city-context"
            className="px-3 py-1.5 border border-slate-300 rounded-lg bg-white text-sm">
            <option value="">Any</option>
            {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </label>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto py-6 space-y-6" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="max-w-2xl mx-auto text-center pt-8">
            <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-6 h-6 text-sky-600" />
            </div>
            <h2 className="text-xl font-display font-semibold text-slate-900">Ask me anything about STR laws</h2>
            <p className="text-sm text-slate-600 mt-1 mb-6">Cites specific ordinance codes for NYC, LA, SF, Miami Beach, Austin, and Nashville.</p>
            <div className="grid sm:grid-cols-2 gap-2 text-left max-w-xl mx-auto">
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => send(s)} data-testid={`suggestion-${i}`}
                  className="text-sm text-slate-700 bg-white border border-slate-200 hover:border-sky-400 hover:text-sky-700 rounded-lg p-3 transition text-left">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`msg-${m.role}-${i}`}>
            {m.role === "assistant" && <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center flex-shrink-0"><Bot className="w-4 h-4 text-sky-400" /></div>}
            <div className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
              <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}{streaming && i === messages.length - 1 && m.role === "assistant" && <span className="inline-block w-1.5 h-4 bg-slate-400 ml-0.5 animate-pulse" />}</div>
            </div>
            {m.role === "user" && <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0"><User className="w-4 h-4 text-slate-700" /></div>}
          </div>
        ))}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="border-t border-slate-200 pt-4">
        <div className="relative bg-white border border-slate-300 rounded-2xl focus-within:border-sky-500 transition">
          <textarea rows={2} value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
            placeholder="Ask about permits, taxes, occupancy caps…" data-testid="chat-input"
            className="w-full px-4 py-3 pr-28 outline-none resize-none rounded-2xl" />
          <MicButton disabled={streaming} onFinish={refreshUsage} onText={(t) => setInput((cur) => (cur.trim() ? `${cur.trim()} ${t}` : t))} />
          <button type="submit" disabled={streaming || !input.trim()} data-testid="chat-send"
            className="absolute right-2 bottom-2 w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center disabled:opacity-40 hover:bg-slate-800 transition">
            <Send className="w-4 h-4" />
          </button>
        </div>
        <div className="text-[11px] text-slate-500 mt-2 text-center">
          Not legal advice. Verify with your city's official portal.
          {usage && (
            <span data-testid="ai-usage" className="ml-2 font-mono">
              · {Math.max(0, usage.chat.limit - usage.chat.used)}/{usage.chat.limit} questions · {Math.max(0, usage.voice.limit - usage.voice.used)}/{usage.voice.limit} voice left today
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

async function transcribe(blob) {
  const form = new FormData();
  form.append("file", blob, `question.${blob.type.includes("mp4") ? "mp4" : "webm"}`);
  const { data } = await api.post("/transcribe", form);
  return data.text;
}

function MicButton({ disabled, onText, onFinish }) {
  const [state, setState] = useState("idle");
  const recRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      const rec = recRef.current;
      if (rec && rec.state === "recording") { rec.onstop = null; rec.stop(); rec.stream.getTracks().forEach((t) => t.stop()); }
    };
  }, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") { toast.error("Voice input isn't supported in this browser"); return; }
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch { toast.error("Microphone access was blocked. Allow it in your browser settings."); return; }
    const mimeType = ["audio/webm", "audio/mp4"].find((t) => MediaRecorder.isTypeSupported(t));
    const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data.size) { chunks.push(e.data); } };
    rec.onstop = async () => {
      clearTimeout(timerRef.current);
      stream.getTracks().forEach((t) => t.stop());
      setState("transcribing");
      try {
        const text = await transcribe(new Blob(chunks, { type: (rec.mimeType || "audio/webm").split(";")[0] }));
        if (text) { onText(text); } else { toast("Didn't catch that. Try again."); }
      } catch (err) {
        toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Transcription failed");
      } finally { setState("idle"); onFinish(); }
    };
    rec.start();
    recRef.current = rec;
    setState("recording");
    timerRef.current = setTimeout(() => { if (rec.state === "recording") { rec.stop(); } }, MAX_RECORD_MS);
  };

  const stop = () => { if (recRef.current?.state === "recording") { recRef.current.stop(); } };
  const recording = state === "recording";
  return (
    <button type="button" onClick={recording ? stop : start} disabled={disabled || state === "transcribing"}
      aria-label={recording ? "Stop recording" : "Ask by voice"} title={recording ? "Stop recording" : "Ask by voice"}
      data-testid="chat-mic-btn" data-state={state}
      className={`absolute right-14 bottom-2 w-10 h-10 rounded-xl flex items-center justify-center transition-colors disabled:opacity-40 ${recording ? "bg-rose-500 text-white animate-pulse" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
      {state === "transcribing" ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
    </button>
  );
}
